import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';

export interface DocumentPatientInfo {
  patientName?: string;
  patientMrn?: string;
  providerName?: string;
  dateOfService?: string;
}

export interface NormalizedRecommendations {
  tests: any[];
  medications: any[];
  treatments: any[];
  risk_factors: any[];
  considerations: string;
}

/**
 * Generates the "Doctor Notes" and "Visit Transcription" PDFs.
 * Single source of truth so every place that offers these downloads (transcription
 * modal, live operations dashboard, ...) produces the exact same document.
 */
@Injectable({
  providedIn: 'root',
})
export class ClinicalDocumentsPdfService {
  // ── Normalization (mirrors TranscriptionModalComponent's private helpers) ────

  private safeJsonParse(value: any): any {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private toArray(value: any): any[] {
    const parsed = this.safeJsonParse(value);
    if (Array.isArray(parsed)) return parsed;
    if (parsed === null || parsed === undefined || parsed === '') return [];
    if (typeof parsed === 'object') return [parsed];
    if (typeof parsed === 'string') {
      const parts = parsed
        .split(/\n|;/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
      return parts.length ? parts : [parsed];
    }
    return [parsed];
  }

  private toText(value: any): string {
    const parsed = this.safeJsonParse(value);
    if (typeof parsed === 'string') return parsed;
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0)
        .join(', ');
    }
    if (parsed && typeof parsed === 'object') {
      return Object.values(parsed)
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0)
        .join(', ');
    }
    return '';
  }

  private normalizeRecommendationEntry(entry: any, type: string): any {
    if (entry === null || entry === undefined) {
      return { related_to: '', primary: '', secondary: '' };
    }
    if (typeof entry === 'string' || typeof entry === 'number') {
      return { related_to: '', primary: String(entry), secondary: '' };
    }

    const related_to = this.toText(entry.related_to ?? entry.relatedTo ?? '');
    let primary = '';
    let secondary = '';

    if (type === 'tests') {
      primary = this.toText(entry.name ?? entry.test_name ?? entry.test ?? entry.title);
      secondary = this.toText(entry.reason ?? entry.description);
    } else if (type === 'medications') {
      primary = this.toText(entry.name ?? entry.medication ?? entry.drug ?? entry.title);
      secondary = [entry.dosage, entry.purpose, entry.reason]
        .map((item) => this.toText(item))
        .filter((item) => item.length > 0)
        .join(' | ');
    } else if (type === 'treatments') {
      primary = this.toText(entry.name ?? entry.treatment ?? entry.title);
      secondary = this.toText(entry.description ?? entry.reason ?? entry.purpose);
    } else {
      primary = this.toText(entry.factor ?? entry.risk ?? entry.name ?? entry.title);
      secondary = this.toText(entry.description ?? entry.reason);
    }

    if (!primary) {
      primary = Object.values(entry)
        .map((item) => this.toText(item))
        .filter((item) => item.length > 0)
        .slice(0, 2)
        .join(' - ');
    }

    return { related_to, primary, secondary };
  }

  private normalizeRecommendationEntries(entries: any[], type: string): any[] {
    return entries
      .map((entry) => this.normalizeRecommendationEntry(entry, type))
      .filter((entry) => entry.primary || entry.secondary || entry.related_to);
  }

  /** Public so the view modal can render the same normalized shape as the PDF. */
  normalizeRecommendations(rawParsedData: any): NormalizedRecommendations {
    const parsedData = this.safeJsonParse(rawParsedData);
    const recSource =
      parsedData?.recommendations ??
      parsedData?.recommendation ??
      parsedData?.Recommendations ??
      parsedData?.doctor_notes?.recommendations ??
      parsedData?.doctor_notes?.recommendation ??
      parsedData?.doctor_notes?.Recommendations ??
      {};
    const recommendations = this.safeJsonParse(recSource);

    return {
      tests: this.normalizeRecommendationEntries(
        this.toArray(recommendations?.tests ?? recommendations?.Tests ?? recommendations?.test ?? recommendations?.Test),
        'tests'
      ),
      medications: this.normalizeRecommendationEntries(
        this.toArray(recommendations?.medications ?? recommendations?.Medications ?? recommendations?.medication ?? recommendations?.Medication),
        'medications'
      ),
      treatments: this.normalizeRecommendationEntries(
        this.toArray(recommendations?.treatments ?? recommendations?.Treatments ?? recommendations?.treatment ?? recommendations?.Treatment),
        'treatments'
      ),
      risk_factors: this.normalizeRecommendationEntries(
        this.toArray(recommendations?.risk_factors ?? recommendations?.riskFactors ?? recommendations?.risk_factor ?? recommendations?.['Risk Factors']),
        'risk_factors'
      ),
      considerations: this.toText(recommendations?.considerations ?? recommendations?.Considerations),
    };
  }

  /** Public so the view modal can render the same bulleted list as the PDF. */
  normalizeAssessmentPoints(rawParsedData: any): string[] {
    const parsedData = this.safeJsonParse(rawParsedData);
    const assessmentPlan = parsedData?.doctor_notes?.['Assessment/Plan'];
    if (!assessmentPlan) return [];

    if (Array.isArray(assessmentPlan)) {
      return assessmentPlan.filter((point) => point && point.trim && point.trim().length > 0);
    }
    if (typeof assessmentPlan === 'string') {
      return assessmentPlan
        .split(/(?:\.\s(?=\d+\.\s))|(?:\n(?=\d+\.\s))/)
        .map((point) => point.trim())
        .filter((point) => point.length > 0);
    }
    return [];
  }

  // ── Doctor Notes PDF ─────────────────────────────────────────────────────

  generateDoctorNotesPdf(
    rawParsedData: any,
    info: DocumentPatientInfo,
    fileName: string = 'doctor-notes.pdf'
  ): void {
    const parsedData = this.safeJsonParse(rawParsedData);
    if (!parsedData) return;

    const dn = parsedData?.doctor_notes ?? {};
    const rec = this.normalizeRecommendations(parsedData);
    const assessPoints = this.normalizeAssessmentPoints(parsedData);
    const actionItems: any[] = parsedData?.action_items ?? [];

    const pdf   = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const mX    = 15;
    const mY    = 18;
    const cW    = pageW - mX * 2;
    const lh    = 5.5;
    let   y     = mY;

    const guard = (need: number) => {
      if (y + need > pageH - mY) { pdf.addPage(); y = mY; }
    };

    const pdfTitle = (text: string) => {
      pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(10, 36, 99);
      const ls = pdf.splitTextToSize(text, cW);
      guard(ls.length * 8 + 6);
      pdf.text(ls, mX, y); y += ls.length * 8 + 1;
      pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.6);
      pdf.line(mX, y, pageW - mX, y); y += 6;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfSection = (text: string) => {
      guard(55); y += 2;
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(10, 36, 99);
      pdf.roundedRect(mX - 2, y - 5.5, cW + 4, 8, 1.5, 1.5, 'F');
      pdf.text(text.toUpperCase(), mX + 1, y); y += 8 + 3;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfSub = (text: string) => {
      guard(20); y += 2;
      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(225, 232, 252);
      pdf.roundedRect(mX - 2, y - 5.5, cW + 4, 7.5, 1, 1, 'F');
      pdf.setTextColor(10, 36, 99);
      pdf.text(text, mX + 1, y); y += 8 + 2;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfLabelVal = (label: string, value: string) => {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const lw = pdf.getTextWidth(`${label}: `);
      pdf.setFont('helvetica', 'normal');
      const vl = pdf.splitTextToSize(value || '—', cW - lw);
      if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(60, 60, 60);
      if (vl.length === 1 && lw + pdf.getTextWidth(vl[0]) <= cW) {
        pdf.text(`${label}:`, mX, y);
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
        pdf.text(vl[0], mX + lw, y); y += lh + 1;
      } else {
        pdf.text(`${label}:`, mX, y); y += lh;
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
        vl.forEach((line: string) => {
          if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
          pdf.text(line, mX + 4, y); y += lh;
        });
        y += 1;
      }
      pdf.setTextColor(0, 0, 0);
    };

    const pdfPara = (text: string, indent = 0) => {
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
      const ls = pdf.splitTextToSize(text, cW - indent);
      ls.forEach((line: string) => {
        if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
        pdf.text(line, mX + indent, y); y += lh;
      });
      y += 2;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfBullet = (text: string) => {
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
      const ls = pdf.splitTextToSize(`•  ${text}`, cW - 5);
      ls.forEach((line: string) => {
        if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
        pdf.text(line, mX + 5, y); y += lh;
      });
      y += 1;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfTable = (headers: string[], rows: string[][], colRatios: number[]) => {
      if (!rows.length) return;
      const fSize   = 9;
      const cellPad = 2;
      const rowLh   = 4.5;
      const totalR  = colRatios.reduce((s, r) => s + r, 0);
      const colW    = colRatios.map(r => (r / totalR) * cW);
      const allRows = [headers, ...rows];
      let tableTop  = y;

      pdf.setFontSize(fSize);
      allRows.forEach((cells, ri) => {
        const isHeader  = ri === 0;
        const cellLines = colW.map((w, ci) => pdf.splitTextToSize(cells[ci] ?? '', w - cellPad * 2));
        const maxLines  = Math.max(1, ...cellLines.map(l => l.length));
        const rowH      = maxLines * rowLh + cellPad * 2;

        if (y + rowH > pageH - mY) {
          pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.4);
          pdf.rect(mX, tableTop, cW, y - tableTop, 'S');
          pdf.addPage(); y = mY; tableTop = y;
        }

        const rowTop = y;
        if (isHeader)       { pdf.setFillColor(10, 36, 99); }
        else if (ri % 2 === 0) { pdf.setFillColor(243, 246, 255); }
        else                { pdf.setFillColor(255, 255, 255); }
        pdf.rect(mX, rowTop, cW, rowH, 'F');

        pdf.setFontSize(fSize);
        if (isHeader) { pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255); }
        else          { pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40); }

        let xCursor = mX;
        cellLines.forEach((lines, ci) => {
          lines.forEach((line, li) => {
            pdf.text(line, xCursor + cellPad, rowTop + cellPad + (li + 1) * rowLh - 0.5);
          });
          xCursor += colW[ci];
        });

        pdf.setDrawColor(isHeader ? 30 : 200, isHeader ? 60 : 215, isHeader ? 130 : 240);
        pdf.setLineWidth(0.15);
        let sepX = mX;
        for (let ci = 1; ci < colW.length; ci++) {
          sepX += colW[ci - 1];
          pdf.line(sepX, rowTop, sepX, rowTop + rowH);
        }
        if (ri < allRows.length - 1) {
          pdf.setDrawColor(200, 215, 240); pdf.setLineWidth(0.15);
          pdf.line(mX, rowTop + rowH, mX + cW, rowTop + rowH);
        }
        y = rowTop + rowH;
      });

      pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.4);
      pdf.rect(mX, tableTop, cW, y - tableTop, 'S');
      y += 5;
      pdf.setTextColor(0, 0, 0);
    };

    // ── Build document ─────────────────────────────────────────────────────
    pdfTitle('Doctor Notes');

    const infoItems: string[] = [];
    if (info.patientName)  infoItems.push(`Patient: ${info.patientName}`);
    if (info.patientMrn)   infoItems.push(`MRN: ${info.patientMrn}`);
    if (info.dateOfService) infoItems.push(`DOS: ${info.dateOfService}`);
    if (info.providerName) infoItems.push(`Provider: ${info.providerName}`);
    if (infoItems.length) {
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(70, 70, 70);
      pdf.text(infoItems.join('   ·   '), mX, y); y += 8;
      pdf.setTextColor(0, 0, 0);
    }

    pdfSection('Clinical Notes');
    if (dn['History Of Present Illness']) {
      pdfSub('History of Present Illness');
      pdfPara(String(dn['History Of Present Illness']));
    }
    if (dn['Physical Examination']) {
      pdfSub('Physical Examination');
      pdfPara(String(dn['Physical Examination']));
    }
    if (dn['Diagnostics']) {
      pdfSub('Diagnostics');
      pdfPara(String(dn['Diagnostics']));
    }
    if (assessPoints.length) {
      pdfSub('Assessment / Plan');
      assessPoints.forEach((p: string) => pdfBullet(p));
    }

    const hasRec = rec.tests?.length || rec.medications?.length ||
                   rec.treatments?.length || rec.risk_factors?.length || rec.considerations;
    if (hasRec) {
      pdfSection('Recommendations');
      if (rec.tests?.length) {
        pdfSub('Tests');
        pdfTable(['Related To', 'Test / Study', 'Reason'], rec.tests.map((i: any) => [i.related_to || '', i.primary || '', i.secondary || '']), [2, 2.5, 3.5]);
      }
      if (rec.medications?.length) {
        pdfSub('Medications');
        pdfTable(['Related To', 'Medication', 'Details'], rec.medications.map((i: any) => [i.related_to || '', i.primary || '', i.secondary || '']), [2, 2.5, 3.5]);
      }
      if (rec.treatments?.length) {
        pdfSub('Treatments');
        pdfTable(['Related To', 'Treatment', 'Description'], rec.treatments.map((i: any) => [i.related_to || '', i.primary || '', i.secondary || '']), [2, 2.5, 3.5]);
      }
      if (rec.risk_factors?.length) {
        pdfSub('Risk Factors');
        pdfTable(['Related To', 'Risk Factor'], rec.risk_factors.map((i: any) => [i.related_to || '', i.primary || '']), [3, 5]);
      }
      if (rec.considerations) {
        pdfSub('Considerations');
        pdfPara(rec.considerations);
      }
    }

    if (actionItems.length) {
      pdfSection(`Action Items  (${actionItems.length})`);
      actionItems.forEach((item: any, idx: number) => {
        guard(30);
        pdf.setFontSize(10); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(10, 36, 99);
        const title = item.name || item.actions || '';
        pdf.text(`${idx + 1}.  ${title}`, mX, y); y += 6;
        pdf.setTextColor(0, 0, 0);
        if (item.actions && item.actions !== title)     pdfLabelVal('Action', item.actions);
        if (item.description)                           pdfLabelVal('Description', item.description);
        if (Array.isArray(item.keywords) && item.keywords.length) {
          pdfLabelVal('Keywords', item.keywords.join(', '));
        }
        y += 3;
      });
    }

    const total = pdf.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      pdf.setPage(p);
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(160, 160, 160);
      pdf.text(`Page ${p} of ${total}`, pageW - mX, pageH - 6, { align: 'right' });
      if (infoItems.length) {
        const [footerInfo] = pdf.splitTextToSize(infoItems.join('   ·   '), cW - 30);
        pdf.text(footerInfo, mX, pageH - 6);
      }
    }

    pdf.save(fileName);
  }

  // ── Transcription PDF ────────────────────────────────────────────────────

  generateTranscriptionPdf(transcriptionData: string, info: DocumentPatientInfo): void {
    if (!transcriptionData) return;

    const pdf   = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const mX    = 15;
    const mY    = 18;
    const cW    = pageW - mX * 2;
    let   y     = mY;

    const guard = (need: number) => {
      if (y + need > pageH - mY) { pdf.addPage(); y = mY; }
    };

    const palette: Array<{ badge: [number, number, number]; bubble: [number, number, number] }> = [
      { badge: [10,  36,  99],  bubble: [232, 240, 255] },
      { badge: [15, 110,  90],  bubble: [218, 248, 238] },
      { badge: [140, 50,  10],  bubble: [255, 235, 222] },
      { badge: [90,  25, 140],  bubble: [244, 232, 255] },
      { badge: [10,  90, 130],  bubble: [220, 242, 255] },
    ];

    pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(10, 36, 99);
    pdf.text('Visit Transcription', mX, y);
    y += 8;
    pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.6);
    pdf.line(mX, y, pageW - mX, y);
    y += 5;

    const infoItems: string[] = [];
    if (info.patientName)  infoItems.push(`Patient: ${info.patientName}`);
    if (info.patientMrn)   infoItems.push(`MRN: ${info.patientMrn}`);
    if (info.dateOfService) infoItems.push(`DOS: ${info.dateOfService}`);
    if (info.providerName) infoItems.push(`Provider: ${info.providerName}`);
    if (infoItems.length) {
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(70, 70, 70);
      pdf.text(infoItems.join('   ·   '), mX, y);
      y += 8;
    }

    interface Turn { label: string; idx: number; text: string; }
    const turns: Turn[]        = [];
    const speakerMap           = new Map<string, number>();
    let   nextIdx              = 0;
    const speakerRe            = /^(Speaker\s+\d+)\s*:\s*/i;
    let   current: Turn | null = null;

    for (const raw of String(transcriptionData).split('\n')) {
      const line = raw.trim();
      if (!line) { if (current) { turns.push(current); current = null; } continue; }
      const m = speakerRe.exec(line);
      if (m) {
        if (current) turns.push(current);
        const lbl = m[1].replace(/\s+/g, ' ');
        if (!speakerMap.has(lbl)) speakerMap.set(lbl, nextIdx++);
        current = { label: lbl, idx: speakerMap.get(lbl)!, text: line.slice(m[0].length).trim() };
      } else if (current) {
        current.text += ' ' + line;
      } else {
        turns.push({ label: '', idx: -1, text: line });
      }
    }
    if (current) turns.push(current);

    const lh        = 5;
    const bubblePad = 3;

    turns.forEach(turn => {
      const pal       = turn.idx >= 0 ? palette[turn.idx % palette.length] : { badge: [100, 100, 100] as [number, number, number], bubble: [245, 245, 245] as [number, number, number] };
      const textLines: string[] = pdf.splitTextToSize(turn.text || '', cW - 8);
      const bubbleH   = textLines.length * lh + bubblePad * 2;

      guard(Math.min((turn.label ? 8 : 0) + bubbleH + 5, pageH - mY * 2));

      if (turn.label) {
        pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold');
        const [br, bg, bb] = pal.badge;
        const bw = pdf.getTextWidth(turn.label) + 6;
        pdf.setFillColor(br, bg, bb);
        pdf.roundedRect(mX, y - 4.5, bw, 6, 1.2, 1.2, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.text(turn.label, mX + 3, y);
        y += 5;
        pdf.setTextColor(0, 0, 0);
      }

      const [r, g, b] = pal.bubble;
      if (y + bubbleH > pageH - mY) {
        textLines.forEach((line: string) => {
          if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
          pdf.setFillColor(r, g, b);
          pdf.rect(mX, y - lh + 1.5, cW, lh, 'F');
          pdf.setFontSize(9.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
          pdf.text(line, mX + 4, y);
          y += lh;
        });
      } else {
        pdf.setFillColor(r, g, b);
        pdf.roundedRect(mX, y - 1, cW, bubbleH, 1.5, 1.5, 'F');
        pdf.setFontSize(9.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
        textLines.forEach((line: string, i: number) => {
          pdf.text(line, mX + 4, y + bubblePad + i * lh + lh - 1.5);
        });
        y += bubbleH + 4;
      }
      pdf.setTextColor(0, 0, 0);
    });

    const total = pdf.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      pdf.setPage(p);
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(160, 160, 160);
      pdf.text(`Page ${p} of ${total}`, pageW / 2, pageH - 6, { align: 'center' });
      pdf.text('Visit Transcription', mX, pageH - 6);
    }

    pdf.save('transcription.pdf');
  }

  /** Speaker turns parsed out of raw transcription text — used by the view modal to render bubbles. */
  parseSpeakerTurns(transcriptionData: string): { label: string; idx: number; text: string }[] {
    const turns: { label: string; idx: number; text: string }[] = [];
    const speakerMap = new Map<string, number>();
    let nextIdx = 0;
    const speakerRe = /^(Speaker\s+\d+)\s*:\s*/i;
    let current: { label: string; idx: number; text: string } | null = null;

    for (const raw of String(transcriptionData || '').split('\n')) {
      const line = raw.trim();
      if (!line) { if (current) { turns.push(current); current = null; } continue; }
      const m = speakerRe.exec(line);
      if (m) {
        if (current) turns.push(current);
        const lbl = m[1].replace(/\s+/g, ' ');
        if (!speakerMap.has(lbl)) speakerMap.set(lbl, nextIdx++);
        current = { label: lbl, idx: speakerMap.get(lbl)!, text: line.slice(m[0].length).trim() };
      } else if (current) {
        current.text += ' ' + line;
      } else {
        turns.push({ label: '', idx: -1, text: line });
      }
    }
    if (current) turns.push(current);
    return turns;
  }

  // ── Billing Summary PDF ──────────────────────────────────────────────────

  generateBillingSummaryPdf(billingData: any, info: DocumentPatientInfo): void {
    if (!billingData) return;

    const pdf   = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const mX    = 15;
    const mY    = 18;
    const cW    = pageW - mX * 2;
    const lh    = 5.5;
    let   y     = mY;

    const guard = (need: number) => {
      if (y + need > pageH - mY) { pdf.addPage(); y = mY; }
    };

    const pdfSection = (text: string) => {
      guard(55); y += 2;
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(10, 36, 99);
      pdf.roundedRect(mX - 2, y - 5.5, cW + 4, 8, 1.5, 1.5, 'F');
      pdf.text(text.toUpperCase(), mX + 1, y); y += 8 + 3;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfPara = (text: string) => {
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
      const ls = pdf.splitTextToSize(text, cW);
      ls.forEach((line: string) => {
        if (y + lh > pageH - mY) { pdf.addPage(); y = mY; }
        pdf.text(line, mX, y); y += lh;
      });
      y += 2;
      pdf.setTextColor(0, 0, 0);
    };

    const pdfCodeTable = (codes: any[]) => {
      if (!codes?.length) return;
      const fSize   = 9;
      const cellPad = 2;
      const rowLh   = 4.5;
      // CPT Code | Description | ICD-10 Codes | Reason  →  ratios 1.2 : 3 : 2 : 3
      const ratios  = [1.2, 3, 2, 3];
      const totalR  = ratios.reduce((s, r) => s + r, 0);
      const colW    = ratios.map(r => (r / totalR) * cW);
      const headers = ['CPT Code', 'Description', 'ICD-10 Codes', 'Reason'];
      const rows    = codes.map(c => [
        c.cpt_code  ?? '',
        c.description ?? '',
        Array.isArray(c.icd10_codes) ? c.icd10_codes.join(', ') : (c.icd10_codes ?? ''),
        c.reason ?? '',
      ]);
      const allRows = [headers, ...rows];
      let tableTop  = y;

      pdf.setFontSize(fSize);
      allRows.forEach((cells, ri) => {
        const isHeader  = ri === 0;
        const cellLines = colW.map((w, ci) => pdf.splitTextToSize(cells[ci], w - cellPad * 2));
        const maxLines  = Math.max(1, ...cellLines.map(l => l.length));
        const rowH      = maxLines * rowLh + cellPad * 2;

        if (y + rowH > pageH - mY) {
          pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.4);
          pdf.rect(mX, tableTop, cW, y - tableTop, 'S');
          pdf.addPage(); y = mY; tableTop = y;
        }

        const rowTop = y;
        if (isHeader)          { pdf.setFillColor(10, 36, 99); }
        else if (ri % 2 === 0) { pdf.setFillColor(243, 246, 255); }
        else                   { pdf.setFillColor(255, 255, 255); }
        pdf.rect(mX, rowTop, cW, rowH, 'F');

        pdf.setFontSize(fSize);
        if (isHeader) { pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255); }
        else          { pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40); }

        let xCursor = mX;
        cellLines.forEach((lines, ci) => {
          lines.forEach((line, li) => {
            if (!isHeader && ci === 0) { pdf.setFont('helvetica', 'bold'); }
            pdf.text(line, xCursor + cellPad, rowTop + cellPad + (li + 1) * rowLh - 0.5);
            if (!isHeader && ci === 0) { pdf.setFont('helvetica', 'normal'); }
          });
          xCursor += colW[ci];
        });

        pdf.setDrawColor(isHeader ? 30 : 200, isHeader ? 60 : 215, isHeader ? 130 : 240);
        pdf.setLineWidth(0.15);
        let sepX = mX;
        for (let ci = 1; ci < colW.length; ci++) {
          sepX += colW[ci - 1];
          pdf.line(sepX, rowTop, sepX, rowTop + rowH);
        }
        if (ri < allRows.length - 1) {
          pdf.setDrawColor(200, 215, 240); pdf.setLineWidth(0.15);
          pdf.line(mX, rowTop + rowH, mX + cW, rowTop + rowH);
        }
        y = rowTop + rowH;
      });

      pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.4);
      pdf.rect(mX, tableTop, cW, y - tableTop, 'S');
      y += 5;
      pdf.setTextColor(0, 0, 0);
    };

    // ── Title ─────────────────────────────────────────────────────────────
    pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(10, 36, 99);
    pdf.text('Billing Analysis', mX, y);
    y += 8;
    pdf.setDrawColor(10, 36, 99); pdf.setLineWidth(0.6);
    pdf.line(mX, y, pageW - mX, y); y += 5;
    pdf.setTextColor(0, 0, 0);

    const infoItems: string[] = [];
    if (info.patientName)  infoItems.push(`Patient: ${info.patientName}`);
    if (info.patientMrn)   infoItems.push(`MRN: ${info.patientMrn}`);
    if (info.dateOfService) infoItems.push(`DOS: ${info.dateOfService}`);
    if (info.providerName) infoItems.push(`Provider: ${info.providerName}`);
    if (infoItems.length) {
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(70, 70, 70);
      pdf.text(infoItems.join('   ·   '), mX, y); y += 8;
      pdf.setTextColor(0, 0, 0);
    }

    if (billingData.cpt_codes?.length) {
      pdfSection('CPT Codes');
      pdfCodeTable(billingData.cpt_codes);
    }

    if (billingData.add_on_codes?.length) {
      pdfSection('Add-On Codes');
      pdfCodeTable(billingData.add_on_codes);
    }

    if (billingData.billing_report) {
      pdfSection('Billing Report');
      pdfPara(String(billingData.billing_report));
    }

    const total = pdf.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      pdf.setPage(p);
      pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(160, 160, 160);
      pdf.text(`Page ${p} of ${total}`, pageW / 2, pageH - 6, { align: 'center' });
      pdf.text('Billing Analysis', mX, pageH - 6);
    }

    pdf.save('billing-analysis.pdf');
  }
}
